import { useEffect, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bell, BellOff, CheckCircle2, AlertTriangle, RefreshCw, Smartphone } from "lucide-react";
import { isNativePlatform } from "@/lib/platform";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

type PermState = "granted" | "denied" | "prompt" | "prompt-with-rationale" | "unknown";

interface TokenRow {
  id: string;
  platform: string | null;
  last_seen_at: string | null;
}

/**
 * Push status diagnostic. Native-only surface: on web this collapses to a
 * one-line "web build — push not applicable" note. On Android it shows:
 *  - runtime permission state (granted / denied / prompt)
 *  - whether ANY push_tokens row exists for the current user (with last-seen)
 *  - actionable next step (open system Settings, retry registration, etc.)
 */
export function PushStatusCard() {
  const { user } = useAuth();
  const native = isNativePlatform();
  const [perm, setPerm] = useState<PermState>("unknown");
  const [tokens, setTokens] = useState<TokenRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      if (native) {
        try {
          const mod = await import("@capacitor/push-notifications");
          const r = await mod.PushNotifications.checkPermissions();
          setPerm((r.receive as PermState) ?? "unknown");
        } catch {
          setPerm("unknown");
        }
      }
      const { data } = await supabase
        .from("push_tokens" as any)
        .select("id, platform, last_seen_at")
        .eq("user_id", user.id)
        .order("last_seen_at", { ascending: false });
      setTokens((data as unknown as TokenRow[]) ?? []);
    } finally {
      setLoading(false);
    }
  }, [user?.id, native]);

  useEffect(() => { refresh(); }, [refresh]);

  const retry = async () => {
    if (!user?.id) return;
    setBusy(true);
    try {
      const { registerPushForUser } = await import("@/lib/push/registerPush");
      await registerPushForUser(user.id);
      // Give the FCM registration listener a beat to write the row.
      await new Promise((r) => setTimeout(r, 1200));
      await refresh();
      toast.success("Registration retried");
    } catch (e) {
      toast.error("Retry failed", { description: String((e as Error)?.message ?? e) });
    } finally {
      setBusy(false);
    }
  };

  const hasToken = !!tokens && tokens.length > 0;
  const lastSeen = tokens?.[0]?.last_seen_at ?? null;

  // Overall verdict
  let verdict: { label: string; tone: "ok" | "warn" | "bad"; hint: string };
  if (!native) {
    verdict = {
      label: "Web build",
      tone: "warn",
      hint: "Push notifications require the Android/iOS app. This diagnostic reflects the app install on your phone; open it there to see status.",
    };
  } else if (perm === "denied") {
    verdict = {
      label: "Blocked by system",
      tone: "bad",
      hint: "Android is blocking notifications for LaneRunner. Open App info → Notifications and enable both the master toggle and the “LaneRunner alerts” channel. If the toggle is grayed out, fully uninstall then reinstall the app.",
    };
  } else if (perm !== "granted") {
    verdict = {
      label: "Permission not granted yet",
      tone: "warn",
      hint: "Tap “Retry registration” to prompt for permission. If no prompt appears, Android has already denied it — go to App info → Notifications and enable it manually.",
    };
  } else if (!hasToken) {
    verdict = {
      label: "Permission granted, no token registered",
      tone: "warn",
      hint: "Permission looks good but Firebase hasn’t issued a token yet. Tap “Retry registration”. If it still fails, sign out and sign in again.",
    };
  } else {
    verdict = {
      label: "Push is working",
      tone: "ok",
      hint: `Your device is registered${lastSeen ? ` (last seen ${new Date(lastSeen).toLocaleString()})` : ""}.`,
    };
  }

  const badgeColor =
    verdict.tone === "ok"
      ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30"
      : verdict.tone === "warn"
      ? "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30"
      : "bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/30";

  const Icon =
    verdict.tone === "ok" ? CheckCircle2 : verdict.tone === "bad" ? BellOff : AlertTriangle;

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Smartphone className="w-5 h-5 text-primary" />
          <div>
            <h3 className="text-base font-semibold">Push status (Android app)</h3>
            <p className="text-xs text-muted-foreground">Diagnostic for lock-screen / notification-shade alerts on your phone.</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={refresh} disabled={loading}>
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <div className={`rounded-md border p-3 ${badgeColor}`}>
        <div className="flex items-start gap-2">
          <Icon className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold">{verdict.label}</div>
            <div className="text-xs mt-1 opacity-90">{verdict.hint}</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4 text-xs">
        <div className="rounded-md border border-border p-3">
          <div className="text-muted-foreground">Platform</div>
          <div className="font-medium mt-0.5">{native ? "Native (Android/iOS)" : "Web"}</div>
        </div>
        <div className="rounded-md border border-border p-3">
          <div className="text-muted-foreground">Permission</div>
          <div className="font-medium mt-0.5 capitalize">
            {native ? perm.replace(/-/g, " ") : "n/a"}
          </div>
        </div>
        <div className="rounded-md border border-border p-3">
          <div className="text-muted-foreground">Device tokens registered</div>
          <div className="font-medium mt-0.5">
            {tokens === null ? "…" : hasToken ? `${tokens.length} • ${tokens[0].platform ?? "unknown"}` : "None"}
          </div>
        </div>
      </div>

      {native && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={retry} disabled={busy || !user?.id}>
            {busy ? "Retrying…" : "Retry registration"}
          </Button>
          <Badge variant="outline" className="text-[10px]">
            Channel: lanerunner_default
          </Badge>
        </div>
      )}

      {!native && (
        <p className="text-xs text-muted-foreground mt-3">
          Open the LaneRunner app on your phone to see and repair push state.
        </p>
      )}
    </Card>
  );
}
